/**
 * Phase 5-B — Billing API client.
 *
 * Thin fetch wrapper matching `identity-api.ts` / `slack-api.ts`
 * conventions. RTK Query is intentionally NOT used — kept consistent until
 * Phase 6.
 *
 * Auth header: `Authorization: Bearer ${state.auth.agency.accessToken}`.
 * The token lives in Redux ONLY (never localStorage).
 *
 * Endpoints (Phase 5-A backend):
 *   GET /api/v1/billing/usage             — current month's billable units
 *   GET /api/v1/billing/history?months=N  — last N months of counts
 *   GET /api/v1/billing/clients           — agency: list of billed clients
 *   GET /api/v1/billing/seats             — enterprise: list of billed seats
 *   GET /api/v1/billing/settings          — billing e-mail + whether the caller may edit it
 *   PUT /api/v1/billing/settings          — owner: set or clear the billing e-mail
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';

// ---------- shared types ----------

export type BillableType = 'agency' | 'enterprise';
export type BillableUnit = 'connected_client' | 'user_seat';

export interface UsageResponse {
  type: BillableType;
  billable_unit: BillableUnit;
  count: number;
}

export interface HistoryEntry {
  month: string; // 'YYYY-MM'
  count: number;
}

export interface BilledClient {
  slug: string;
  activated_at: string; // ISO date
}

export interface BilledSeat {
  employee_id: string;
  email: string;
  activated_at: string; // ISO date
}

export interface BillingSettings {
  billing_email: string | null;
  can_edit: boolean;
}

export type BillingStripeSyncStatus = 'updated' | 'skipped' | 'failed';

export interface BillingStripeSync {
  status: BillingStripeSyncStatus;
  reason: string | null;
}

export interface UpdateBillingSettingsResult extends BillingSettings {
  /** What happened to the Stripe customer; null when the server sent none. */
  stripe: BillingStripeSync | null;
  /** The server's outcome text, shown to the user as is. */
  message: string;
}

/** Error thrown by every call here; `fieldErrors` is filled on a 422. */
export type BillingApiError = Error & {
  status?: number;
  fieldErrors?: Record<string, string[]>;
};

// ---------- internal helpers ----------

function getAuthHeader(token?: string): Record<string, string> {
  const t = token ?? store.getState().auth.agency.accessToken;
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (t) {
    headers['Authorization'] = `Bearer ${t}`;
  }
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[billing-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  tokenOverride?: string
): Promise<T> {
  const headers: Record<string, string> = {
    ...getAuthHeader(tokenOverride),
    ...((init.headers as Record<string, string>) || {}),
  };

  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let detail = '';
    let fieldErrors: Record<string, string[]> | undefined;
    try {
      const body = await parseJson<{
        message?: string;
        error?: string;
        errors?: Record<string, string[] | string>;
      }>(response);
      detail = body?.message || body?.error || '';
      if (body?.errors && typeof body.errors === 'object') {
        fieldErrors = {};
        for (const [field, value] of Object.entries(body.errors)) {
          fieldErrors[field] = Array.isArray(value) ? value : [String(value)];
        }
      }
    } catch {
      // swallow — fall back to status text
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as BillingApiError;
    err.status = response.status;
    if (fieldErrors) {
      err.fieldErrors = fieldErrors;
    }
    throw err;
  }

  return parseJson<T>(response);
}

function unwrap<T>(raw: T | { data?: T }): T {
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>) &&
    (raw as { data?: T }).data !== undefined
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

// ---------- public API ----------

export async function getUsage(token?: string): Promise<UsageResponse> {
  const raw = await request<UsageResponse | { data?: UsageResponse }>(
    'billing/usage',
    { method: 'GET' },
    token
  );
  return unwrap<UsageResponse>(raw);
}

export async function getHistory(
  months: number,
  token?: string
): Promise<HistoryEntry[]> {
  const params = new URLSearchParams();
  params.set('months', String(months));
  const raw = await request<
    HistoryEntry[] | { data?: HistoryEntry[]; history?: HistoryEntry[] }
  >(`billing/history?${params.toString()}`, { method: 'GET' }, token);
  if (Array.isArray(raw)) return raw;
  if (raw && 'history' in raw && Array.isArray(raw.history)) return raw.history;
  return unwrap<HistoryEntry[]>(raw as { data?: HistoryEntry[] }) ?? [];
}

export async function getClients(token?: string): Promise<BilledClient[]> {
  const raw = await request<
    BilledClient[] | { data?: BilledClient[]; clients?: BilledClient[] }
  >('billing/clients', { method: 'GET' }, token);
  if (Array.isArray(raw)) return raw;
  if (raw && 'clients' in raw && Array.isArray(raw.clients)) return raw.clients;
  return unwrap<BilledClient[]>(raw as { data?: BilledClient[] }) ?? [];
}

export async function getSeats(token?: string): Promise<BilledSeat[]> {
  const raw = await request<
    BilledSeat[] | { data?: BilledSeat[]; seats?: BilledSeat[] }
  >('billing/seats', { method: 'GET' }, token);
  if (Array.isArray(raw)) return raw;
  if (raw && 'seats' in raw && Array.isArray(raw.seats)) return raw.seats;
  return unwrap<BilledSeat[]>(raw as { data?: BilledSeat[] }) ?? [];
}

export async function getBillingSettings(
  token?: string
): Promise<BillingSettings> {
  const raw = await request<BillingSettings | { data?: BillingSettings }>(
    'billing/settings',
    { method: 'GET' },
    token
  );
  const settings = unwrap<BillingSettings>(raw);
  return {
    billing_email: settings?.billing_email || null,
    can_edit: settings?.can_edit === true,
  };
}

/**
 * Saves the organisation's billing e-mail; an empty value clears it.
 *
 * The result keeps the server's `message` and `stripe` outcome: a save can
 * succeed while the Stripe customer was not updated (`stripe.status` =
 * `failed`). A 422 rejects with `fieldErrors.billing_email`.
 */
export async function updateBillingSettings(
  billingEmail: string | null,
  token?: string
): Promise<UpdateBillingSettingsResult> {
  const value = (billingEmail ?? '').trim();
  const raw = await request<{
    data?: Partial<BillingSettings> & { stripe?: BillingStripeSync | null };
    message?: string;
  }>(
    'billing/settings',
    {
      method: 'PUT',
      body: JSON.stringify({ billing_email: value === '' ? null : value }),
    },
    token
  );
  const data = raw?.data ?? {};
  return {
    billing_email: data.billing_email || null,
    can_edit: data.can_edit === true,
    stripe: data.stripe ?? null,
    message: raw?.message ?? '',
  };
}
